import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ShoppingCart, FileText, Package, Plus, Loader2,
  Clock, XCircle, Eye, Edit, Send,
  ClipboardList, ArrowRight, CheckCircle2
} from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import TalepGecmisiModal from '@/components/TalepGecmisiModal';
import { useSekme } from '@/lib/geri';

export default function Purchasing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Sekme adreste (`?tab=`) — okunuyordu ama YAZILMIYORDU: Siparişler sekmesinden
  // bir siparişe gidip geri dönen kullanıcı Talepler sekmesine düşüyordu.
  const [activeTab, setActiveTab] = useSekme('requests', 'tab');

  // Purchase Requests State
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [talepDurumu, setTalepDurumu] = useState('all');
  const [gecmisAcik, setGecmisAcik] = useState(false);

  // Purchase Orders State
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [approvedRequests, setApprovedRequests] = useState([]);

  // Goods Receipt State
  const [bekleyenler, setBekleyenler] = useState([]);   // siparis+tedarikci basina acik kayitlar
  const [receipts, setReceipts] = useState([]);         // fiili teslimatlar
  const [receiptsLoading, setReceiptsLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'requests') fetchRequests();
    else if (activeTab === 'orders') fetchOrders();
    else if (activeTab === 'receipts') fetchReceipts();
  }, [activeTab, talepDurumu]);

  // AKIS: Talep --onaylandi--> Siparis --gonderildi--> Mal Kabul
  // Her kayit TEK bir sekmede durur: onaylanan talep Talepler'den cikip
  // Siparisler sekmesindeki "siparis bekleyen" tablosuna, gonderilen siparis
  // de Siparisler'den cikip Mal Kabul'e gecer.
  const ACIK_TALEP_DURUMLARI = 'draft,pending_cost_control,pending_manager,rejected';

  const fetchRequests = async () => {
    setRequestsLoading(true);
    try {
      const params = { status: talepDurumu !== 'all' ? talepDurumu : ACIK_TALEP_DURUMLARI };
      const res = await api.get('/purchase-requests', { params });
      setRequests(res.data || []);
    } catch (err) {
      toast.error('Talepler yüklenemedi');
    } finally {
      setRequestsLoading(false);
    }
  };

  // Siparisler sekmesi: henuz GONDERILMEMIS siparisler + siparis bekleyen
  // onayli talepler. Gonderilen siparis buradan cikar, Mal Kabul'e gecer.
  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const [siparisler, onayliTalepler] = await Promise.all([
        api.get('/purchase-orders', { params: { status: 'draft' } }),
        api.get('/purchase-requests', { params: { status: 'approved' } }),
      ]);
      setOrders(siparisler.data || []);
      setApprovedRequests(onayliTalepler.data || []);
    } catch (err) {
      toast.error('Siparişler yüklenemedi');
    } finally {
      setOrdersLoading(false);
    }
  };

  // Mal Kabul sekmesi: ustte bekleyenler (siparis + tedarikci basina bir satir),
  // altta fiili teslimatlar.
  const fetchReceipts = async () => {
    setReceiptsLoading(true);
    try {
      const [acik, tamamlanan] = await Promise.all([
        api.get('/purchase-receipts', { params: { kayit_turu: 'bekleyen', status: 'pending,partial' } }),
        api.get('/purchase-receipts', { params: { kayit_turu: 'kabul' } }),
      ]);
      setBekleyenler(acik.data || []);
      setReceipts(tamamlanan.data || []);
    } catch (err) {
      toast.error('Mal kabul kayıtları yüklenemedi');
    } finally {
      setReceiptsLoading(false);
    }
  };

  const handleSendOrder = async (orderId) => {
    try {
      const res = await api.patch(`/purchase-orders/${orderId}/send`);
      const adet = res.data?.bekleyen_kayitlar?.length || 1;
      toast.success(
        adet > 1
          ? `Sipariş gönderildi — ${adet} tedarikçi için ayrı mal kabul açıldı`
          : 'Sipariş gönderildi, Mal Kabul sekmesine geçti');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { label: 'Teslim Bekliyor', className: 'bg-yellow-100 text-yellow-800' },
      'pending_cost_control': { label: 'Maliyet Onayı', className: 'bg-yellow-100 text-yellow-800' },
      'pending_manager': { label: 'Yönetici Onayı', className: 'bg-orange-100 text-orange-800' },
      'pending_warehouse': { label: 'Depo Onayı', className: 'bg-amber-100 text-amber-800' },
      'approved': { label: 'Onaylandı', className: 'bg-green-100 text-green-800' },
      'rejected': { label: 'Reddedildi', className: 'bg-red-100 text-red-800' },
      'completed': { label: 'Tamamlandı', className: 'bg-blue-100 text-blue-800' },
      'draft': { label: 'Taslak', className: 'bg-gray-100 text-gray-800' },
      'sent': { label: 'Gönderildi', className: 'bg-purple-100 text-purple-800' },
      'partial': { label: 'Kısmi Teslim', className: 'bg-orange-100 text-orange-800' },
      'received': { label: 'Teslim Alındı', className: 'bg-blue-100 text-blue-800' },
      'ordered': { label: 'Sipariş Verildi', className: 'bg-blue-100 text-blue-800' },
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  // Receipt Approval Handlers
  const handleApproveReceiptWarehouse = async (receiptId) => {
    try {
      await api.post(`/purchase-receipts/${receiptId}/approve-warehouse`);
      toast.success('Depo onayı verildi');
      fetchReceipts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
  };

  const handleApproveReceiptManager = async (receiptId) => {
    try {
      await api.post(`/purchase-receipts/${receiptId}/approve-manager`);
      toast.success('Yönetici onayı verildi, mal kabul tamamlandı');
      fetchReceipts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
  };

  const handleRejectReceipt = async (receiptId, reason = '') => {
    try {
      await api.post(`/purchase-receipts/${receiptId}/reject`, { reason });
      toast.success('Mal kabul reddedildi');
      fetchReceipts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="purchasing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1916]">Sipariş</h1>
          <p className="text-sm text-[#8a7e70]">Satın alma talepleri, siparişler ve mal kabul işlemleri</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="requests">
            <ShoppingCart size={16} className="mr-2" /> Satın Alma Talepleri
          </TabsTrigger>
          <TabsTrigger value="orders">
            <FileText size={16} className="mr-2" /> Siparişler
          </TabsTrigger>
        </TabsList>

        {/* Satın Alma Talepleri Tab */}
        <TabsContent value="requests" className="space-y-4 mt-4">
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
                <ShoppingCart size={18} /> Satın Alma Talepleri
                <span className="text-xs font-normal text-[#8a7e70]">
                  · onaylananlar Siparişler sekmesinde
                </span>
              </CardTitle>
              <div className="flex items-center gap-2">
                <Select value={talepDurumu} onValueChange={setTalepDurumu}>
                  <SelectTrigger className="w-52 bg-white border-[#d0c4b0]" data-testid="request-status-filter">
                    <SelectValue placeholder="Durum filtrele" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Durumlar</SelectItem>
                    <SelectItem value="draft">Taslak</SelectItem>
                    <SelectItem value="pending_cost_control">Maliyet Onayı Bekliyor</SelectItem>
                    <SelectItem value="pending_manager">Yönetici Onayı Bekliyor</SelectItem>
                    <SelectItem value="rejected">Reddedildi</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => setGecmisAcik(true)}
                  className="border-[#d0c4b0] bg-white text-[#3F3E39] hover:bg-[#e8dfd0]"
                  data-testid="talep-gecmis-btn">
                  <Clock size={16} className="mr-2" /> Geçmiş
                </Button>
                <Link to="/erp/purchase-requests/new">
                  <Button className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]" data-testid="new-request-btn">
                    <Plus size={16} className="mr-2" /> Yeni Talep
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {requestsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin" size={24} />
                </div>
              ) : requests.length === 0 ? (
                <div className="text-center py-12 text-[#8a7e70]">
                  <ShoppingCart size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Bekleyen talep yok</p>
                  <p className="text-xs mt-1">Onaylanan talepler Siparişler sekmesinde</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#e8dfd0]">
                      <TableHead className="text-[#1a1916] font-bold">Talep No</TableHead>
                      <TableHead className="text-[#1a1916] font-bold">Tarih</TableHead>
                      <TableHead className="text-[#1a1916] font-bold">Maliyet Merkezi</TableHead>
                      <TableHead className="text-[#1a1916] font-bold">Kalem Sayısı</TableHead>
                      <TableHead className="text-[#1a1916] font-bold">Tahmini Tutar</TableHead>
                      <TableHead className="text-[#1a1916] font-bold">Durum</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((req) => (
                      // Aksiyonlar talebin KENDİ sayfasında ("İşlemler" menüsü);
                      // liste sadece görüntüler, satıra tıklayınca talep açılır.
                      <TableRow key={req.id} className="hover:bg-[#f5f0e8] cursor-pointer"
                        onClick={() => navigate(`/erp/purchase-requests/${req.id}`)}
                        data-testid={`request-row-${req.id}`}>
                        <TableCell className="font-mono">{req.request_no || req.id?.slice(0, 8)}</TableCell>
                        <TableCell>{new Date(req.created_at).toLocaleDateString('tr-TR')}</TableCell>
                        <TableCell>{req.cost_center_name || '-'}</TableCell>
                        <TableCell>{req.items?.length || 0}</TableCell>
                        <TableCell className="font-semibold">
                          {(req.total_estimated_cost || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TRY
                        </TableCell>
                        <TableCell>{getStatusBadge(req.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Siparişler Tab */}
        <TabsContent value="orders" className="space-y-4 mt-4">
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
                <FileText size={18} /> Gönderilmemiş Siparişler
              </CardTitle>
              {/* Talepsiz siparis acma korunuyor; talepten acma asagidaki
                  "Sipariş Bekleyen Onaylı Talepler" tablosundan yapiliyor. */}
              <Link to="/erp/purchase-orders/new">
                <Button className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]" data-testid="new-order-btn">
                  <Plus size={16} className="mr-2" /> Yeni Sipariş
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="p-0">
              {ordersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin" size={24} />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12 text-[#8a7e70]">
                  <FileText size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Gönderilmeyi bekleyen sipariş yok</p>
                  <p className="text-xs mt-1">Gönderilen siparişler Mal Kabul sekmesinde</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#e8dfd0]">
                      <TableHead className="text-[#1a1916] font-bold">Sipariş No</TableHead>
                      <TableHead className="text-[#1a1916] font-bold">Tarih</TableHead>
                      <TableHead className="text-[#1a1916] font-bold">Tedarikçi</TableHead>
                      <TableHead className="text-[#1a1916] font-bold">Kalem Sayısı</TableHead>
                      <TableHead className="text-[#1a1916] font-bold">Toplam</TableHead>
                      <TableHead className="text-[#1a1916] font-bold">Durum</TableHead>
                      <TableHead className="text-[#1a1916] font-bold text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id} className="hover:bg-[#f5f0e8]">
                        <TableCell className="font-mono">{order.order_no || order.id?.slice(0, 8)}</TableCell>
                        <TableCell>{new Date(order.created_at).toLocaleDateString('tr-TR')}</TableCell>
                        <TableCell>{order.supplier_name || '-'}</TableCell>
                        <TableCell>{order.items?.length || 0}</TableCell>
                        <TableCell className="font-semibold">
                          {(order.total_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {order.currency || 'TRY'}
                        </TableCell>
                        <TableCell>{getStatusBadge(order.status)}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSendOrder(order.id)}
                            className="text-[#3d6e4e] hover:bg-[#3d6e4e]/10"
                            data-testid={`send-order-${order.id}`}
                          >
                            <Send size={14} className="mr-1" /> Gönder
                          </Button>
                          <Link to={`/erp/purchase-orders/${order.id}`}>
                            <Button variant="ghost" size="sm" data-testid={`view-order-${order.id}`}>
                              <Eye size={14} />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Onaylanan talepler buraya gecti — Talepler sekmesinde artik
              gorunmuyorlar. Her satirdan dogrudan siparis acilir. */}
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="border-b border-[#d0c4b0]">
              <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
                <ClipboardList size={18} /> Sipariş Bekleyen Onaylı Talepler
                {approvedRequests.length > 0 && (
                  <Badge className="bg-[#3d6e4e]/10 text-[#3d6e4e]">{approvedRequests.length}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {approvedRequests.length === 0 ? (
                <div className="text-center py-8 text-[#8a7e70]">
                  <p>Sipariş bekleyen onaylı talep yok</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#e8dfd0]">
                      <TableHead className="text-[#1a1916] font-bold">Talep No</TableHead>
                      <TableHead className="text-[#1a1916] font-bold">Tarih</TableHead>
                      <TableHead className="text-[#1a1916] font-bold">Maliyet Merkezi</TableHead>
                      <TableHead className="text-[#1a1916] font-bold">Kalem</TableHead>
                      <TableHead className="text-[#1a1916] font-bold">Tahmini Tutar</TableHead>
                      <TableHead className="text-[#1a1916] font-bold text-right">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {approvedRequests.map((req) => (
                      <TableRow key={req.id} className="hover:bg-[#f5f0e8]">
                        <TableCell className="font-mono">
                          <Link to={`/erp/purchase-requests/${req.id}`}
                            className="hover:underline" data-testid={`approved-request-${req.id}`}>
                            {req.request_no || req.id?.slice(0, 8)}
                          </Link>
                        </TableCell>
                        <TableCell>{new Date(req.created_at).toLocaleDateString('tr-TR')}</TableCell>
                        <TableCell>{req.cost_center_name || '-'}</TableCell>
                        <TableCell>{req.items?.length || 0}</TableCell>
                        <TableCell className="font-semibold">
                          {(req.total_estimated_cost || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TRY
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm"
                            onClick={() => navigate(`/erp/purchase-orders/new?request_id=${req.id}`)}
                            className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]"
                            data-testid={`create-order-${req.id}`}>
                            <ArrowRight size={14} className="mr-1" /> Sipariş Oluştur
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mal Kabul Tab */}
        {/* Mal Kabul sekmesi KALDIRILDI — mal kabul artik kendi menusunde
            (/erp/mal-kabul) ve yeni teslimat belgesi akisiyla calisiyor:
            irsaliye / veresiye fisi / siparis fisi, AI ile okuma, faturaya
            kalem+miktar duzeyinde baglama. Bu sayfa yalnizca TALEP ve SIPARIS. */}
      </Tabs>

      <TalepGecmisiModal open={gecmisAcik} onOpenChange={setGecmisAcik} />
    </div>
  );
}
